"use client"

import { useState, useEffect, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { ChevronDown, ImageIcon, Save, Loader2 } from "lucide-react"
import { ImageUpload } from "@/components/ui/image-upload"
import { useAuth } from "@/hooks/use-auth"
import { usePlatformLogo } from "@/hooks/use-platform-logo"
import { useSystemSettings } from "@/hooks/use-system-settings"
import { useTranslations } from "next-intl"



const FAVICON_ACCEPT = "image/svg+xml,image/png,image/jpeg,image/webp,image/x-icon,image/vnd.microsoft.icon"
const FAVICON_MAX_SIZE_MB = 0.5

export function LogoSettingsSection() {

  const tUi = useTranslations("SystemSettings.SystemSettingsLogoSettingsSection")

  const { fetchWithAuth } = useAuth()
  const { logoUrl, isCustom, refreshLogo } = usePlatformLogo()
  const { getSettingValue, updateSetting, isLoading: settingsLoading } = useSystemSettings()
  const [isUploading, setIsUploading] = useState(false)
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null)

  // Platform name state
  const [platformName, setPlatformName] = useState("")
  const [isSavingName, setIsSavingName] = useState(false)
  const [nameStatus, setNameStatus] = useState<{ type: "success" | "error"; message: string } | null>(null)

  // Favicon state
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null)
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false)
  const [faviconStatus, setFaviconStatus] = useState<{ type: "success" | "error"; message: string } | null>(null)

  useEffect(() => {
    if (!settingsLoading) {
      setPlatformName(getSettingValue("platform_name", ""))
    }
  }, [settingsLoading, getSettingValue])

  const savedName = getSettingValue("platform_name", "")
  const nameChanged = platformName.trim() !== savedName

  const handleSaveName = async () => {
    setIsSavingName(true)
    setNameStatus(null)
    try {
      await updateSetting("platform_name", platformName.trim())
      setNameStatus({ type: "success", message: tUi("platformNameSavedRefreshThePageToSee") })
      setTimeout(() => setNameStatus(null), 3000)
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save"
      setNameStatus({ type: "error", message })
      setTimeout(() => setNameStatus(null), 3000)
    } finally {
      setIsSavingName(false)
    }
  }

  const fetchFavicon = useCallback(async () => {
    try {
      const res = await fetch("/api/system/favicon")
      if (!res.ok) return
      const { url } = await res.json()
      setFaviconUrl(url)
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    fetchFavicon()
  }, [fetchFavicon])

  const clearStatus = () => {
    setTimeout(() => setStatus(null), 3000)
  }

  const clearFaviconStatus = () => {
    setTimeout(() => setFaviconStatus(null), 3000)
  }

  const handleUpload = async (file: File) => {
    setIsUploading(true)
    setStatus(null)
    try {
      const formData = new FormData()
      formData.append("file", file)

      const res = await fetchWithAuth("/api/system/logo", {
        method: "POST",
        body: formData,
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || "Upload failed")
      }

      await refreshLogo()
      setStatus({ type: "success", message: tUi("logoUploadedSuccessfully") })
      clearStatus()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed"
      setStatus({ type: "error", message })
      clearStatus()
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = async () => {
    setIsUploading(true)
    setStatus(null)
    try {
      const res = await fetchWithAuth("/api/system/logo", {
        method: "DELETE",
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || "Delete failed")
      }

      await refreshLogo()
      setStatus({ type: "success", message: tUi("logoRemovedDefaultRestored") })
      clearStatus()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Delete failed"
      setStatus({ type: "error", message })
      clearStatus()
    } finally {
      setIsUploading(false)
    }
  }

  const handleFaviconUpload = async (file: File) => {
    setIsUploadingFavicon(true)
    setFaviconStatus(null)
    try {
      const formData = new FormData()
      formData.append("file", file)

      const res = await fetchWithAuth("/api/system/favicon", {
        method: "POST",
        body: formData,
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || "Upload failed")
      }

      const { url } = await res.json()
      setFaviconUrl(url)
      setFaviconStatus({ type: "success", message: tUi("faviconUploadedRefreshThePageToSeeIt") })
      clearFaviconStatus()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed"
      setFaviconStatus({ type: "error", message })
      clearFaviconStatus()
    } finally {
      setIsUploadingFavicon(false)
    }
  }

  const handleFaviconRemove = async () => {
    setIsUploadingFavicon(true)
    setFaviconStatus(null)
    try {
      const res = await fetchWithAuth("/api/system/favicon", {
        method: "DELETE",
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || "Delete failed")
      }

      setFaviconUrl(null)
      setFaviconStatus({ type: "success", message: tUi("faviconRemovedDefaultRestoredRefreshThePageTo") })
      clearFaviconStatus()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Delete failed"
      setFaviconStatus({ type: "error", message })
      clearFaviconStatus()
    } finally {
      setIsUploadingFavicon(false)
    }
  }

  return (
    <Collapsible defaultOpen={false}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                <span><span key={tUi("platformBranding")}>{tUi("platformBranding")}</span></span>
              </div>
              <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            {/* Platform Name */}
            <div className="space-y-3">
              <Label htmlFor="platform-name" className="text-base font-medium"><span key={tUi("platformName")}>{tUi("platformName")}</span></Label>
              <p className="text-sm text-muted-foreground">
                <span key={tUi("displayedInTheBrowserTabTitleLeaveEmpty")}>{tUi("displayedInTheBrowserTabTitleLeaveEmpty")}</span></p>
              <div className="flex gap-2">
                <Input
                  id="platform-name"
                  value={platformName}
                  onChange={(e) => setPlatformName(e.target.value)}
                  placeholder={"Versafy"}
                  className="max-w-sm"
                />
                <Button
                  onClick={handleSaveName}
                  disabled={!nameChanged || isSavingName}
                  size="sm"
                >
                  {isSavingName ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  <span><span key={tUi("save")}>{tUi("save")}</span></span>
                </Button>
              </div>
              {nameStatus && (
                <Alert className={nameStatus.type === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
                  <AlertDescription className={nameStatus.type === "success" ? "text-green-800" : "text-red-800"}>
                    {nameStatus.message}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="border-t" />

            {/* Logo */}
            <div className="space-y-3">
              <Label className="text-base font-medium"><span key={tUi("logo")}>{tUi("logo")}</span></Label>
              <p className="text-sm text-muted-foreground">
                <span key={tUi("uploadACustomLogoForThePlatformIt")}>{tUi("uploadACustomLogoForThePlatformIt")}</span></p>

              <ImageUpload
                currentImageUrl={logoUrl}
                onUpload={handleUpload}
                onRemove={handleRemove}
                isUploading={isUploading}
              />

              {isCustom && (
                <p className="text-xs text-muted-foreground">
                  <span key={tUi("customLogoIsActiveRemoveToRestoreThe")}>{tUi("customLogoIsActiveRemoveToRestoreThe")}</span></p>
              )}

              {status && (
                <Alert className={status.type === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
                  <AlertDescription className={status.type === "success" ? "text-green-800" : "text-red-800"}>
                    {status.message}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="border-t" />

            {/* Favicon */}
            <div className="space-y-3">
              <Label className="text-base font-medium"><span key={tUi("favicon")}>{tUi("favicon")}</span></Label>
              <p className="text-sm text-muted-foreground">
                <span key={tUi("uploadACustomFaviconBrowserTabIconRecommended")}>{tUi("uploadACustomFaviconBrowserTabIconRecommended")}</span></p>

              <ImageUpload
                currentImageUrl={faviconUrl}
                onUpload={handleFaviconUpload}
                onRemove={handleFaviconRemove}
                isUploading={isUploadingFavicon}
                accept={FAVICON_ACCEPT}
                maxSizeMB={FAVICON_MAX_SIZE_MB}
              />

              {faviconUrl && (
                <p className="text-xs text-muted-foreground">
                  <span key={tUi("customFaviconIsActiveRemoveToRestoreThe")}>{tUi("customFaviconIsActiveRemoveToRestoreThe")}</span></p>
              )}

              {faviconStatus && (
                <Alert className={faviconStatus.type === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
                  <AlertDescription className={faviconStatus.type === "success" ? "text-green-800" : "text-red-800"}>
                    {faviconStatus.message}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
